'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Cookie, X, Settings } from 'lucide-react';
import { cookieConsent } from '@/services/cookieConsentService';

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    functional: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already given consent
    const hasConsent = cookieConsent.hasConsent();
    setIsVisible(!hasConsent);

    // Load current preferences if they exist
    const current = cookieConsent.getConsent();
    if (current) {
      setPreferences({
        functional: current.functional,
        analytics: current.analytics,
        marketing: current.marketing,
      });
    }
  }, []);

  const handleAcceptAll = () => {
    cookieConsent.acceptAll();
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    cookieConsent.rejectAll();
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    cookieConsent.saveConsent(preferences);
    setIsVisible(false);
  };

  const togglePreference = (key: keyof typeof preferences) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 pb-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          {showSettings ? (
            /* Settings Panel */
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                  <Settings className="h-6 w-6 text-blue-600 mr-2" />
                  Cookie Preferences
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Back"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Essential Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <div className="flex items-center mb-1">
                      <h4 className="font-semibold text-gray-900">Essential Cookies</h4>
                      <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                        Always Active
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Required for the website to function. Includes authentication, security, and session management.
                    </p>
                  </div>
                </div>

                {/* Functional Cookies */}
                <div className="flex items-start justify-between p-4 bg-white rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">Functional Cookies</h4>
                    <p className="text-sm text-gray-600">
                      Remember your preferences like theme, language, and dashboard layout.
                    </p>
                  </div>
                  <label htmlFor="functional-cookies" className="relative inline-flex items-center cursor-pointer ml-4">
                    <span className="sr-only">Toggle functional cookies</span>
                    <input
                      id="functional-cookies"
                      type="checkbox"
                      checked={preferences.functional}
                      onChange={() => togglePreference('functional')}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between p-4 bg-white rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">Analytics Cookies</h4>
                    <p className="text-sm text-gray-600">
                      Help us understand how you use the platform to improve performance and features.
                    </p>
                  </div>
                  <label htmlFor="analytics-cookies" className="relative inline-flex items-center cursor-pointer ml-4">
                    <span className="sr-only">Toggle analytics cookies</span>
                    <input
                      id="analytics-cookies"
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={() => togglePreference('analytics')}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-start justify-between p-4 bg-white rounded-lg border border-gray-200 opacity-50">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">Marketing Cookies</h4>
                    <p className="text-sm text-gray-600">
                      Not currently used. We don't track you for advertising purposes.
                    </p>
                  </div>
                  <label htmlFor="marketing-cookies" className="relative inline-flex items-center cursor-not-allowed ml-4">
                    <span className="sr-only">Toggle marketing cookies (disabled)</span>
                    <input
                      id="marketing-cookies"
                      type="checkbox"
                      checked={false}
                      disabled
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 rounded-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5"></div>
                  </label>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleSavePreferences}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Save Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Accept All
                </button>
                <Link
                  href="/cookies"
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cookie Policy
                </Link>
              </div>
            </div>
          ) : (
            /* Simple Banner */
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start flex-1">
                  <Cookie className="h-6 w-6 text-blue-600 mt-1 mr-4 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      We Value Your Privacy
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      We use cookies to enhance your experience, keep you logged in, and analyze our traffic. 
                      We don't use advertising or third-party tracking cookies.{' '}
                      <Link href="/cookies" className="text-blue-600 hover:underline">
                        Read our Cookie Policy
                      </Link>
                    </p>
                    
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={handleAcceptAll}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        Accept All
                      </button>
                      <button
                        onClick={handleRejectAll}
                        className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                      >
                        Reject All
                      </button>
                      <button
                        onClick={() => setShowSettings(true)}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center"
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Customize
                      </button>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setIsVisible(false)}
                  className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
